"use client";
import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import InputField from "../common/InputField";
import Button from "../common/Button";
import useAuth from "../../hooks/useAuth";
import { Mail, Phone, User } from "lucide-react";

export const BASE_URL = 'https://api.rakeshbansalventures.com'

interface StateOption {
  _id: string;
  state: string;
  state_code: number;
}

interface CityOption {
  _id: string;
  city_name: string;
  city_code: number;
  state_code: number;
}

class PhoneInputBoundary extends React.Component<
  { children: React.ReactNode },
  { errKey: number }
> {
  state = { errKey: 0 };
  static getDerivedStateFromError() { return {}; }
  componentDidCatch() { this.setState(s => ({ errKey: s.errKey + 1 })); }
  render() {
    return (
      <React.Fragment key={this.state.errKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

const SignupForm: React.FC = () => {
  const { handleSignup, loading, error: authError } = useAuth();
    const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dob: "",
    gender: "",
    country: "India",
    country_code: "91",
    state: "",
    state_code: "",
    city: "",
    city_code: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [acceptTerms, setAcceptTerms] = useState(false);

  const [states, setStates] = useState<StateOption[]>([]);
  const [cities, setCities] = useState<CityOption[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "phone" ? value.replace(/\D/g, "").slice(0, 15) : value,
    }));
  };

  const handlePhoneChange = (value: string, countryData: any) => {
    try {
      const digits = String(value || '').replace(/\D/g, '');
      setFormData((prev) => ({
        ...prev,
        phone: digits,
        country: countryData?.name || prev.country,
        country_code: countryData?.dialCode || prev.country_code,
      }));
    } catch {
      // silently ignore library errors from autofill
    }
  };

   const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCode = e.target.value; // string from <select>
    const selected = states.find((s) => String(s.state_code) === selectedCode);
    setFormData((prev) => ({
      ...prev,
      state: selected?.state || "",
      state_code: selectedCode,
      city: "",
      city_code: "",
    }));
  };

const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCode = e.target.value;
    const selected = cities.find((c) => String(c.city_code) === selectedCode);
    setFormData((prev) => ({
      ...prev,
      city: selected?.city_name || "",
      city_code: selectedCode,
    }));
  };
  

  useEffect(() => {
    const state: any = location.state;
    if (state) {
      const { phone, email } = state;
      setFormData((prev) => ({
        ...prev,
        phone: phone ? String(phone).replace(/\D/g, "") : prev.phone,
        email: email || prev.email,
      }));
    }
  }, [location.state]);

  
   // Fetch states on mount
  useEffect(() => {
    const fetchStates = async () => {
      setLoadingStates(true);
      try {
        const res = await fetch(`${BASE_URL}/api/locations/states`);
        const data: any = await res.json();
        const list: StateOption[] = Array.isArray(data)
          ? data
          : data?.states || data?.data || [];
        setStates(list);
      } catch (err) {
        console.error("Failed to fetch states:", err);
      } finally {
        setLoadingStates(false);
      }
    };
    fetchStates();
  }, []);


  // Fetch cities whenever the selected state changes
  useEffect(() => {
    if (!formData.state_code) {
      setCities([]);
      return;
    }
    const fetchCities = async () => {
      setLoadingCities(true);
      try {
        const res = await fetch(
          `${BASE_URL}/api/locations/states/${formData.state_code}/cities`
        );
        const data: any = await res.json();
        const list: CityOption[] = Array.isArray(data)
          ? data
          : data?.cities || data?.data || [];
        setCities(list);
      } catch (err) {
        console.error("Failed to fetch cities:", err);
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, [formData.state_code]);


  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (formData.phone.length < 10 || formData.phone.length > 15) {
      newErrors.phone = "Phone number must be between 10 and 15 digits";
    }

    if (!formData.state_code) {
      newErrors.state = "State is required";
    }

    if (!formData.city_code) {
      newErrors.city = "City is required";
    }

    if (!formData.dob) {
      newErrors.dob = "Date of birth is required";
    } else {
      const today = new Date();
      const dob = new Date(formData.dob);
      const age = today.getFullYear() - dob.getFullYear() -
        (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
      if (age < 13) {
        newErrors.dob = "You must be at least 13 years old";
      }
    }

    if (!formData.gender) {
      newErrors.gender = "Please select your gender";
    }

    if (!acceptTerms) {
      newErrors.terms = "You must accept the terms and conditions";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const digits = String(formData.phone).replace(/\D/g, '');
      const phoneWithPlus = `+${digits}`;
      const payload: any = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        phone: phoneWithPlus,
        country: formData.country,
        country_code: formData.country_code,
        state: formData.state,
        state_code: formData.state_code,
        city: formData.city,
        city_code: formData.city_code,
        gender: formData.gender,
        dob: formData.dob,
        device_id: "web",
        device_name: "web",
        app_version: "1.0.0",
      };

      const response = await fetch(`${BASE_URL}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const res: any = await response.json();
      console.log('Signup response:', res);

      if (res && (res.success || res.status === 200)) {
        navigate('/login', {
          state: { message: res.message || 'Account created successfully! Please sign in.' },
        });
      } else {
        const errMsg = res?.message || 'Registration failed';
        setErrors((prev) => ({ ...prev, form: errMsg }));
      }
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, form: err?.message || 'Registration failed' }));
    }
  };

  return (
    <div className="space-y-6">
      <form className="space-y-5" onSubmit={handleSubmit} autoComplete="off">
        {authError && (
          <div className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl shadow-sm">
            <p className="font-medium">Signup Error</p>
            <p>{authError}</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            type="text"
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            error={errors.firstName}
            placeholder=" Anne"
            icon={<User className="text-gray-400" size={18} />}
          />

          <InputField
            type="text"
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder=" Lee"
            icon={<User className="text-gray-400" size={18} />}
          />
        </div>

        <InputField
          type="email"
          label="Email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
          placeholder=" annlee@example.com"
          icon={<Mail className="text-gray-400" size={18} />}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
          <PhoneInputBoundary>
            <PhoneInput
              country={'in'}
              value={formData.phone}
              onChange={handlePhoneChange}
              inputProps={{ name: 'phone', required: true, autoComplete: 'new-password' }}
              inputClass="w-full"
            />
          </PhoneInputBoundary>
          {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
            <select
              name="state"
              value={formData.state_code}
              onChange={handleStateChange}
              disabled={loadingStates}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
            >
              <option value="">
                {loadingStates ? "Loading states..." : "Select state"}
              </option>
              {states.map((s) => (
                <option key={s._id} value={s.state_code}>
                  {s.state}
                </option>
              ))}
            </select>
            {errors.state && <p className="mt-1 text-sm text-red-600">{errors.state}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
            <select
              name="city"
              value={formData.city_code}
              onChange={handleCityChange}
              disabled={!formData.state_code || loadingCities}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
            >
              <option value="">
                {!formData.state_code
                  ? "Select state first"
                  : loadingCities
                  ? "Loading cities..."
                  : "Select city"}
              </option>
              {cities.map((c) => (
                <option key={c._id} value={c.city_code}>
                  {c.city_name}
                </option>
              ))}
            </select>
            {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
          </div>
        </div>

        <InputField
          type="date"
          label="Date of Birth"
          name="dob"
          value={formData.dob}
          onChange={handleChange}
          error={errors.dob}
          placeholder=""
          icon={<User className="text-gray-400" size={18} />}
          max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 13); return d.toISOString().split('T')[0]; })()}
        />

        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
          <div className="flex items-center space-x-6">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="gender"
                value="male"
                checked={formData.gender === 'male'}
                onChange={handleChange}
                className="form-radio h-4 w-4 text-purple-600"
              />
              <span className="ml-2 text-sm text-gray-700">Male</span>
            </label>

            <label className="inline-flex items-center">
              <input
                type="radio"
                name="gender"
                value="female"
                checked={formData.gender === 'female'}
                onChange={handleChange}
                className="form-radio h-4 w-4 text-purple-600"
              />
              <span className="ml-2 text-sm text-gray-700">Female</span>
            </label>
          </div>
          {errors.gender && <p className="mt-1 text-sm text-red-600">{errors.gender}</p>}
        </div>

        <div className="flex items-start space-x-3 pt-2">
          <input
            id="terms"
            name="terms"
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="h-4 w-4 mt-1 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
          />
          <label htmlFor="terms" className="text-sm text-gray-700 leading-5">
            I agree to the{" "}
            <Link to="/terms" className="text-purple-600 hover:text-purple-500 font-medium">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-purple-600 hover:text-purple-500 font-medium">
              Privacy Policy
            </Link>
          </label>
        </div>
        {errors.terms && (
          <p className="mt-1 text-sm text-red-600">{errors.terms}</p>
        )}

        <div className="pt-4">
          <Button
            type="submit"
            disabled={loading}
            loading={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
          >
            Create Account
          </Button>
        </div>

        <div className="text-sm text-center pt-4 border-t border-gray-200">
          <span className="text-gray-600">Already have an account? </span>
          <Link
            to="/login"
            className="text-purple-600 hover:text-purple-500 font-medium transition-colors duration-200"
          >
            Sign in
          </Link>
        </div>
      </form>

      <div className="mt-4 text-center">
        <button
          onClick={() => navigate('/login')}
          className="text-sm text-purple-600 hover:text-purple-500 font-medium"
        >
          ← Back to Login
        </button>
      </div>
    </div>
  );
};

export default SignupForm;